import { inject, Injectable } from "@angular/core";
import { CategoryView } from "../../../../models/view/category/category.view";
import { BasiqIoTransactionType } from "../../../../models/types/basiq-io-transaction.types";
import { BasiqCategoryAction } from "@bitwarden/web-vault/app/services/syncing/basiq/actions/basiq.action.category";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { BasiqMapType } from "@bitwarden/web-vault/app/models/types/basiq.types";
import { SyncStatusEnum } from "@bitwarden/web-vault/app/models/types/general-types";
import { GlossSyncService } from "@bitwarden/web-vault/app/services/syncing/gloss.sync.service";

export type TransactionCategoryMap = Map<string, CategoryView[]>;

@Injectable({
  providedIn: "root",
})
export class BasiqCategoryMapper {
  private glossSyncService: GlossSyncService;
  private basiqCategoryAction: BasiqCategoryAction;
  transactionCategoryMap: TransactionCategoryMap = new Map();

  constructor() {
    this.basiqCategoryAction = inject(BasiqCategoryAction);
    this.glossSyncService = inject(GlossSyncService);
  }

  async mapCategories(accountView: AccountView, rawBasiqIoTransactions: BasiqIoTransactionType[]) {
    try {
      const mappedBasiqData: BasiqMapType = {
        categories: [],
        transactions: [],
        sourceTransactions: [],
        pendingTransactions: [],
      };

      rawBasiqIoTransactions.forEach((rawBasiqIoTransaction) => {
        this.toCategoryView(rawBasiqIoTransaction, mappedBasiqData.categories);
      });

      return mappedBasiqData.categories;
    } catch (error) {
      await this.glossSyncService.logError(
        [accountView],
        error,
        "mapping transactions",
        SyncStatusEnum.cantMapTransactions,
      );
      return [];
    }
  }

  toCategoryView(
    rawBasiqIoTransaction: BasiqIoTransactionType,
    processedCategories: CategoryView[],
  ) {
    const categoryNames =
      this.basiqCategoryAction.categoryNamesFromBasiqTransaction(rawBasiqIoTransaction);
    const categoryViews: CategoryView[] = [];
    if (categoryNames.length) {
      for (const categoryName of categoryNames) {
        const mappedCategoryName: string =
          this.basiqCategoryAction.getMappedCategoryName(categoryName);
        const existingCategory = this.basiqCategoryAction.categoryViewByName(mappedCategoryName);
        const processedCategory = processedCategories.find(
          (category) => category.name === mappedCategoryName,
        );

        if (processedCategory) {
          categoryViews.push(processedCategory);
        } else if (existingCategory) {
          categoryViews.push(existingCategory);
        } else {
          const newCategory = new CategoryView({
            dc: new Date().toISOString(),
            dm: new Date().toISOString(),
            gd: false,
            sc: "basiq",
            id: crypto.randomUUID(),
            n: mappedCategoryName,
            v: 2,
            vid: null,
          });
          processedCategories.push(newCategory);
          categoryViews.push(newCategory);
        }
      }
    }

    this.transactionCategoryMap.set(rawBasiqIoTransaction.id, categoryViews);
  }
}
